import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { useCatalog, useRefreshAll } from "../../hooks/queries";
import { useLocale } from "../../hooks/useLocale";
import { api, ApiError } from "../../lib/api";
import { isV2 } from "../../lib/config";
import { money } from "../../lib/format";
import { useAutoPrint } from "../../hooks/usePrintReceipt";
import type { CreatedTransaction, SessionRules, SessionView } from "../../lib/types";
import { Modal } from "../ui/Modal";
import { OverrideDialog } from "../v2/OverrideDialog";
import { PaymentSection, usePaymentForm } from "../ui/PaymentSection";
import { useLiveSession } from "./TimeRemaining";

interface BaseProps { session: SessionView; rules: SessionRules; onClose: () => void }

/**
 * Posts a payment; if the server's total differs from ours (the overtime fine ticked over
 * while the dialog was open) we retry once with the server's figure instead of failing.
 */
async function postWithTotal<T>(send: (total: number | null) => Promise<T>, expected: number): Promise<T> {
  try {
    return await send(expected);
  } catch (e) {
    if (e instanceof ApiError && e.code === "PAYMENT_MISMATCH") {
      const total = (e.details as { total?: number } | undefined)?.total;
      if (typeof total === "number") return send(total);
    }
    throw e;
  }
}

export function ExtendDialog({ session, rules, onClose }: BaseProps) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const { toast, toastError } = useToast();
  const refresh = useRefreshAll();
  const autoPrint = useAutoPrint();
  const catalog = useCatalog();
  const live = useLiveSession(session, rules);
  const form = usePaymentForm();
  const [units, setUnits] = useState(1);
  const [busy, setBusy] = useState(false);

  const ext = catalog.data?.find((p) => p.sku === "EXT-30");
  // v2: the server prices the extension from the rule frozen at check-in (tax and any overtime fine included)
  const step = rules.extensionMinutes || 30;
  const quote = useQuery({
    queryKey: ["v2", "extend-quote", session.id, units], enabled: isV2(), staleTime: 0,
    queryFn: () => api.get<{ addedMinutes: number; total: number; overtimeFee: number }>(`/sessions/${session.id}/extend-quote?minutes=${step * units}`),
  });
  const addMinutes = isV2() ? (quote.data?.addedMinutes ?? step * units) : (ext?.durationMinutes ?? 30) * units;
  const fine = isV2() ? (quote.data?.overtimeFee ?? 0) : live.seconds < 0 ? live.fee : 0;
  const total = isV2() ? (quote.data?.total ?? 0) : (ext?.price ?? 0) * units + fine;

  async function submit() {
    setBusy(true);
    try {
      const res = await postWithTotal(
        (tot) => api.post<{ transaction: CreatedTransaction }>(`/sessions/${session.id}/extend`, { minutes: addMinutes, payments: form.payments(tot ?? total) }),
        total,
      );
      toast(t("extend.done", { name: session.kid.name, minutes: addMinutes }), "success");
      refresh();
      autoPrint(res.transaction.id);
      onClose();
    } catch (e) {
      toastError(e);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={t("extend.title", { name: session.kid.name })}
      size="sm"
      footer={
        <>
          <button type="button" className="btn-ghost" onClick={onClose}>{t("common.cancel")}</button>
          <button type="button" className="btn-primary" disabled={busy || (isV2() ? !quote.data : !ext) || !form.isValid(total)} onClick={submit}>{t("extend.confirm", { amount: money(total) })}</button>
        </>
      }
    >
      <div className="flex items-center justify-between rounded-xl bg-purple-50 p-3 text-xs font-bold">
        <span>{t("table.tag")}: <span className="num font-mono">{session.wristbandTag}</span></span>
        <div className="flex items-center gap-2">
          <button type="button" className="btn-ghost !px-2.5" onClick={() => setUnits((u) => Math.max(1, u - 1))} aria-label="−">−</button>
          <span className="num min-w-14 text-center text-sm font-black">+{addMinutes} {t("common.min")}</span>
          <button type="button" className="btn-ghost !px-2.5" onClick={() => setUnits((u) => Math.min(8, u + 1))} aria-label="+">+</button>
        </div>
      </div>
      {fine > 0 && (
        <div className="flex justify-between rounded-xl bg-red-50 p-3 text-xs font-bold text-red-700">
          <span>{t("extend.overtime")}</span>
          <span className="num font-mono">{money(fine)} {t("currency")}</span>
        </div>
      )}
      <div className="flex items-center justify-between text-sm font-black">
        <span>{t("payment.total")}</span>
        <span className="num font-mono text-cosmic-purple">{money(total)} {t("currency")}</span>
      </div>
      <PaymentSection form={form} total={total} />
    </Modal>
  );
}

export function CheckoutDialog({ session, rules, onClose }: BaseProps) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const { toast, toastError } = useToast();
  const { isManager } = useAuth();
  const refresh = useRefreshAll();
  const autoPrint = useAutoPrint();
  const live = useLiveSession(session, rules);
  const form = usePaymentForm();
  const [waive, setWaive] = useState(false);
  const [busy, setBusy] = useState(false);
  /** v2: waiving needs a one-time manager approval (even for a cashier asking a manager). */
  const [override, setOverride] = useState<string | null>(null);
  const [asking, setAsking] = useState(false);

  const fee = waive ? 0 : live.fee;

  async function submit() {
    setBusy(true);
    try {
      const res = await postWithTotal(
        (tot) => api.post<{ stamps: number; stampEarned: boolean; freeStampAvailable: boolean; transaction: CreatedTransaction | null }>(`/sessions/${session.id}/checkout`, { payments: form.payments(tot ?? fee), waiveOvertime: waive, overrideId: waive && isV2() ? (override ?? undefined) : undefined }),
        fee,
      );
      toast(res.freeStampAvailable ? t("checkout.doneFree", { name: session.kid.name }) : t("checkout.done", { name: session.kid.name }), "success");
      if (res.transaction) autoPrint(res.transaction.id);
      refresh();
      onClose();
    } catch (e) {
      toastError(e);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={t("checkout.title", { name: session.kid.name })}
      size="sm"
      footer={
        <>
          <button type="button" className="btn-ghost" onClick={onClose}>{t("common.cancel")}</button>
          <button type="button" className={fee > 0 ? "btn-danger" : "btn-dark"} disabled={busy || !form.isValid(fee) || (waive && isV2() && !override)} onClick={submit}>
            {fee > 0 ? t("checkout.collectAndExit", { amount: money(fee) }) : t("checkout.exit")}
          </button>
        </>
      }
    >
      <div className="rounded-xl bg-gray-50 p-3 text-xs font-bold">
        <div className="flex justify-between"><span>{t("table.tag")}</span><span className="num font-mono">{session.wristbandTag}</span></div>
        <div className="flex justify-between"><span>{t("table.parent")}</span><span>{session.guardian.fullName}</span></div>
        <div className="flex justify-between"><span>{t("loyalty.stamps")}</span><span className="num">{session.kid.stamps} / {session.stampsNeeded ?? 6}</span></div>
      </div>
      {live.fee > 0 ? (
        <>
          <div className="flex items-center justify-between rounded-xl bg-red-50 p-3 text-sm font-black text-red-700">
            <span>{t("checkout.overtimeFee")}</span>
            <span className="num font-mono">{money(live.fee)} {t("currency")}</span>
          </div>
          {(isManager || isV2()) && (
            <label className="flex items-center gap-2 text-xs font-bold">
              <input type="checkbox" className="rounded text-cosmic-purple focus:ring-0" checked={waive} onChange={(e) => { setWaive(e.target.checked); setOverride(null); if (e.target.checked && isV2()) setAsking(true); }} />
              {t("checkout.waive")}
              {waive && override && <span className="text-emerald-700">✓</span>}
            </label>
          )}
          {isV2() && <OverrideDialog open={asking} action="waive_overtime" entity={{ type: "session", id: session.id, amount: live.fee }} onClose={() => { setAsking(false); if (!override) setWaive(false); }} onApproved={(id) => { setOverride(id); setAsking(false); }} />}
          <PaymentSection form={form} total={fee} />
        </>
      ) : (
        <p className="text-xs font-bold text-emerald-700">✅ {t("checkout.noFee")}</p>
      )}
    </Modal>
  );
}
